import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface BatchSelectionContextType {
  selectedItems: Set<string>;
  isSelecting: boolean;
  selectItem: (id: string) => void;
  deselectItem: (id: string) => void;
  selectAll: (ids: string[]) => void;
  addToSelection: (ids: string[]) => void;
  deselectAll: () => void;
  toggleSelection: () => void;
  getSelectedCount: () => number;
  isSelected: (id: string) => boolean;
  getSelectedItems: () => string[];
  // Shift-click support
  setOrderedIds: (ids: string[]) => void;
  clickItem: (id: string, shiftKey: boolean) => void;
}

const BatchSelectionContext = createContext<BatchSelectionContextType | undefined>(undefined);

export const BatchSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isSelecting, setIsSelecting] = useState(false);
  const location = useLocation();
  // For range selection
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [orderedIds, setOrderedIdsState] = useState<string[]>([]);

  // Clear selection when navigating to different routes
  useEffect(() => {
    setSelectedItems(new Set());
    setAnchorId(null);
    setOrderedIdsState([]);
  }, [location.pathname]);

  const selectItem = useCallback((id: string) => {
    setSelectedItems(prev => new Set([...prev, id]));
  }, []);

  const deselectItem = useCallback((id: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setSelectedItems(new Set(ids));
  }, []);

  const addToSelection = useCallback((ids: string[]) => {
    setSelectedItems(prev => new Set([...prev, ...ids]));
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  const toggleSelection = useCallback(() => {
    setIsSelecting(prev => !prev);
  }, []);

  const getSelectedCount = useCallback(() => {
    return selectedItems.size;
  }, [selectedItems]);

  const isSelected = useCallback((id: string) => {
    return selectedItems.has(id);
  }, [selectedItems]);

  const getSelectedItems = useCallback(() => {
    return Array.from(selectedItems);
  }, [selectedItems]);

  const setOrderedIds = useCallback((ids: string[]) => {
    setOrderedIdsState(ids || []);
  }, []);

  const clickItem = useCallback((id: string, shiftKey: boolean) => {
    setSelectedItems(prev => {
      // Shift-click: select range between anchor and current id
      if (shiftKey && anchorId && orderedIds && orderedIds.length > 0) {
        const startIdx = orderedIds.indexOf(anchorId);
        const endIdx = orderedIds.indexOf(id);
        if (startIdx !== -1 && endIdx !== -1) {
          const from = Math.min(startIdx, endIdx);
          const to = Math.max(startIdx, endIdx);
          const rangeIds = orderedIds.slice(from, to + 1);
          const next = new Set(prev);
          for (const rid of rangeIds) next.add(rid);
          return next;
        }
      }

      // Normal click: toggle item and set anchor
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

    // Update anchor only on non-shift clicks
    if (!shiftKey) setAnchorId(id);
  }, [anchorId, orderedIds]);

  const contextValue = useMemo(() => ({
    selectedItems,
    isSelecting,
    selectItem,
    deselectItem,
    selectAll,
    addToSelection,
    deselectAll,
    toggleSelection,
    getSelectedCount,
    isSelected,
    getSelectedItems,
    setOrderedIds,
    clickItem
  }), [
    selectedItems,
    isSelecting,
    selectItem,
    deselectItem,
    selectAll,
    addToSelection,
    deselectAll,
    toggleSelection,
    getSelectedCount,
    isSelected,
    getSelectedItems,
    setOrderedIds,
    clickItem
  ]);

  return (
    <BatchSelectionContext.Provider value={contextValue}>
      {children}
    </BatchSelectionContext.Provider>
  );
};

export const useBatchSelection = () => {
  const context = useContext(BatchSelectionContext);
  if (!context) {
    throw new Error('useBatchSelection must be used within BatchSelectionProvider');
  }
  return context;
}; 